import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getArchitectDetails } from '../services/api';
import debounce from 'lodash.debounce';
import './ProjectPopup.css';

const ProjectPopup = ({ project, isLoading }) => {
  const [hoveredArchitectId, setHoveredArchitectId] = useState(null);
  const [architectInfo, setArchitectInfo] = useState(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [shouldShowExpandButton, setShouldShowExpandButton] = useState(false);
  const [isLoadingArchitect, setIsLoadingArchitect] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const descriptionRef = useRef(null);

  const updateDescriptionExpandState = useCallback(() => {
    if (!descriptionRef.current) return;

    const element = descriptionRef.current;
    const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
    if (Number.isNaN(lineHeight) || lineHeight <= 0) return;

    const maxHeight = 4.8 * lineHeight; // ~3 lignes dans le layout actuel
    const shouldExpand = element.scrollHeight > maxHeight;
    setShouldShowExpandButton(shouldExpand);

    // Réinitialiser l'état d'expansion si le texte est court
    if (!shouldExpand) {
      setIsDescriptionExpanded(false);
    }
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchArchitectInfo = useCallback(async (architectId) => {
    setIsLoadingArchitect(true);
    try {
      console.log('Fetching architect info for ID:', architectId);
      const response = await getArchitectDetails(architectId);
      console.log('Architect info response:', response);
      setArchitectInfo(response);
    } catch (error) {
      console.error('Error fetching architect info:', error);
      setArchitectInfo(null);
    } finally {
      setIsLoadingArchitect(false);
    }
  }, [setIsLoadingArchitect, setArchitectInfo]);

  const debouncedFetchArchitectInfo = useMemo(
    () => debounce(fetchArchitectInfo, 150),
    [fetchArchitectInfo]
  );

  useEffect(() => {
    if (hoveredArchitectId) {
      debouncedFetchArchitectInfo(hoveredArchitectId);
    } else {
      setArchitectInfo(null);
      setIsLoadingArchitect(false);
    }
  }, [hoveredArchitectId, debouncedFetchArchitectInfo]);

  useEffect(() => {
    updateDescriptionExpandState();

    // Mesure après paint pour gérer les cas où le popup finit son layout
    const rafId = window.requestAnimationFrame(updateDescriptionExpandState);
    const timeoutId = window.setTimeout(updateDescriptionExpandState, 120);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [project.description, updateDescriptionExpandState]);

  useEffect(() => {
    if (!descriptionRef.current || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      updateDescriptionExpandState();
    });
    observer.observe(descriptionRef.current);

    return () => observer.disconnect();
  }, [updateDescriptionExpandState]);

  const handleMouseEnter = (architectId) => {
    if (!isMobile) {
      setHoveredArchitectId(architectId);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setHoveredArchitectId(null);
    }
  };

  const handleTouchStart = (architectId) => {
    if (isMobile) {
      setHoveredArchitectId(architectId);
    }
  };

  const handleClose = () => {
    setHoveredArchitectId(null);
  };

  const toggleDescription = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const handleArchitectClick = (architectName) => {
    if (!architectName) return;

    const query = encodeURIComponent(`architecture agency ${architectName}`);
    window.open(`https://www.google.com/search?q=${query}`, '_blank', 'noopener,noreferrer');
  };

  const formatDescription = (description) => {
    if (!description) return 'Description non disponible';
    
    // Expression régulière pour détecter les URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = description.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        // Nettoyer l'URL pour l'affichage
        const url = part.replace(/[.,;]$/, '');
        return (
          <a 
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="description-link"
          >
            {url}
          </a>
        );
      }
      return part;
    });
  };

  if (!project) {
    return <div>Chargement...</div>;
  }

  if (isLoading) {
    return (
      <div className="project-popup loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <div>Chargement des détails...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-popup">
      <h3>{project.titre || 'Titre non disponible'}</h3>
      {project.adresse && <div>{project.adresse}</div>}
      {project.date_realisation && (
        <div>Année de réalisation : {project.date_realisation}</div>
      )}
      {project.architectes && project.architectes.length > 0 ? (
        <div>
          <strong>Architectes: </strong>
          {project.architectes.map((architecte, index) => (
            <span key={architecte.id} style={{ position: 'relative', display: 'inline-block' }}>
              <span
                onMouseEnter={() => handleMouseEnter(architecte.id)}
                onMouseLeave={handleMouseLeave}
                onTouchStart={() => handleTouchStart(architecte.id)}
                onClick={() => handleArchitectClick(architecte.nom)}
                style={{ 
                  cursor: 'pointer', 
                  textDecoration: 'underline', 
                  color: 'blue' 
                }}
              >
                {architecte.nom}
                {index < project.architectes.length - 1 && ', '}
              </span>
              {hoveredArchitectId === architecte.id && (
                <>
                  {isMobile && (
                    <div 
                      className="architect-info-overlay visible"
                      onClick={handleClose}
                    />
                  )}
                  <div 
                    className="architect-info-popup visible"
                    onMouseEnter={() => handleMouseEnter(architecte.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {isMobile && (
                      <button 
                        className="close-button"
                        onClick={handleClose}
                        aria-label="Fermer"
                      >
                        ×
                      </button>
                    )}
                    {isLoadingArchitect ? (
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                        <div>Chargement...</div>
                      </div>
                    ) : architectInfo ? (
                      <>
                        <h3>{architectInfo.nom || 'Nom indisponible'}</h3>
                        {architectInfo.biography && <div>{architectInfo.biography}</div>}
                        {architectInfo.birth_date && (
                          <div>Année de naissance / création : {new Date(architectInfo.birth_date).getFullYear()}</div>
                        )}
                        {architectInfo.death_date && (
                          <div>Année de décès / fermeture : {new Date(architectInfo.death_date).getFullYear()}</div>
                        )}
                        {architectInfo.lien_wikipedia && (
                          <div>
                            <a 
                              href={architectInfo.lien_wikipedia} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Site de l'agene ou de l'architecte
                            </a>
                          </div>
                        )}
                        {!architectInfo.biography && !architectInfo.birth_date && !architectInfo.death_date && !architectInfo.lien_wikipedia && (
                          <div>Aucune information disponible</div>
                        )}
                      </>
                    ) : (
                      <div>Aucune information disponible</div>
                    )}
                  </div>
                </>
              )}
            </span>
          ))}
        </div>
      ) : null}
      <div className="description-container">
        <div 
          ref={descriptionRef}
          className={`description ${isDescriptionExpanded ? 'expanded' : ''}`}
        >
          {formatDescription(project.description)}
        </div>
        {shouldShowExpandButton && (
          <button 
            className="expand-button"
            onClick={toggleDescription}
            aria-expanded={isDescriptionExpanded}
          >
            {isDescriptionExpanded ? 'Afficher moins' : 'Afficher plus'}
          </button>
        )}
      </div>
    </div>
  );
};

export default ProjectPopup;
